import { styled } from 'styled-system/jsx'

const Wrapper = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#EAE4F8',
    borderRadius: 'lg',
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 14px 40px -28px rgba(80, 50, 180, 0.25)',
  },
})

const HeaderRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const HeaderText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const Eyebrow = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'quickpass.primary',
  },
})

const Title = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '16px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.2px',
  },
})

const Form = styled('form', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _focusWithin: {
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const Input = styled('input', {
  base: {
    flex: '1',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '500',
    color: 'quickpass.ink',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    paddingY: '4px',
    _placeholder: { color: '#B8B0CC', opacity: '1' },
  },
})

const SubmitButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '7px',
    cursor: 'pointer',
    transition:
      'transform {durations.fast} {easings.appleStandard}, opacity {durations.fast} {easings.appleStandard}',
    _hover: { transform: 'translateY(-1px)' },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: { transform: 'none' },
    },
  },
})

const Suggestions = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
})

const SuggestionChip = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1px solid #EAE4F8',
    borderRadius: 'pill',
    paddingX: '10px',
    paddingY: '5px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primary',
      color: 'quickpass.primary',
    },
  },
})

const AnswerCard = styled('div', {
  base: {
    backgroundColor: 'quickpass.surfaceCool',
    borderRadius: 'md',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const AnswerHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
})

const QuestionEcho = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const ConfidencePill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    paddingX: '8px',
    paddingY: '3px',
    borderRadius: 'pill',
  },
  variants: {
    confidence: {
      high: {
        backgroundColor: 'rgba(46, 184, 137, 0.15)',
        color: 'quickpass.success',
      },
      medium: {
        backgroundColor: 'rgba(255, 184, 76, 0.18)',
        color: '#A26200',
      },
      low: {
        backgroundColor: 'rgba(214, 66, 66, 0.15)',
        color: 'quickpass.warmth',
      },
    },
  },
})

const AnswerText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.55',
  },
})

const MatchedRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '4px',
  },
})

const MatchedChip = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.primary',
    backgroundColor: 'rgba(128, 84, 255, 0.08)',
    border: '1px solid rgba(128, 84, 255, 0.25)',
    borderRadius: 'pill',
    paddingX: '10px',
    paddingY: '5px',
    cursor: 'pointer',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, border-color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'rgba(128, 84, 255, 0.18)',
      borderColor: 'quickpass.primary',
    },
  },
})

const ErrorText = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.warmth',
    fontWeight: '600',
  },
})

const HintText = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

export {
  Wrapper,
  HeaderRow,
  HeaderText,
  Eyebrow,
  Title,
  Form,
  Input,
  SubmitButton,
  Suggestions,
  SuggestionChip,
  AnswerCard,
  AnswerHeader,
  QuestionEcho,
  ConfidencePill,
  AnswerText,
  MatchedRow,
  MatchedChip,
  ErrorText,
  HintText,
}
